import {
  DOWNLOAD_DB_NAME,
  DOWNLOAD_DB_VERSION,
  DOWNLOAD_INDEXES,
  DOWNLOAD_STORES,
} from "./downloadPersistenceSchema";

const STORE_DEFINITIONS = [
  { name: DOWNLOAD_STORES.jobs, options: { keyPath: "id" } },
  { name: DOWNLOAD_STORES.files, options: { keyPath: "id" } },
  { name: DOWNLOAD_STORES.chunks, options: { keyPath: "id" } },
  { name: DOWNLOAD_STORES.events, options: { keyPath: "id" } },
  { name: DOWNLOAD_STORES.schemaMeta, options: { keyPath: "key" } },
];

const DEFAULT_CHUNK_SIZE_BYTES = 8 * 1024 * 1024;
const TERMINAL_JOB_STATUSES = new Set(["complete", "cancelled"]);

function hasName(names, name) {
  return Boolean(
    names &&
      (typeof names.contains === "function"
        ? names.contains(name)
        : Array.prototype.includes.call(names, name))
  );
}

function ensureIndexes(store) {
  (DOWNLOAD_INDEXES[store.name] || []).forEach((index) => {
    if (!hasName(store.indexNames, index.name)) {
      store.createIndex(index.name, index.keyPath, index.options || {});
    }
  });
}

function ensureSchema(db, transaction) {
  STORE_DEFINITIONS.forEach((definition) => {
    const store = hasName(db.objectStoreNames, definition.name)
      ? transaction.objectStore(definition.name)
      : db.createObjectStore(definition.name, definition.options);

    ensureIndexes(store);
  });
}

function validateFileSize(fileRecord) {
  if (
    !fileRecord ||
    typeof fileRecord.size !== "number" ||
    !Number.isFinite(fileRecord.size) ||
    fileRecord.size < 0
  ) {
    throw new Error("Download file records must include a numeric size >= 0.");
  }
}

function validateUniqueFileIds(files) {
  const fileIds = new Set();

  files.forEach((fileRecord) => {
    if (!fileRecord || !fileRecord.id) {
      throw new Error("Download file records must include an id.");
    }

    if (fileIds.has(fileRecord.id)) {
      throw new Error(`Duplicate download file id: ${fileRecord.id}`);
    }

    fileIds.add(fileRecord.id);
  });
}

function buildChunkRecords({ jobId, files, chunkSizeBytes }) {
  if (
    typeof chunkSizeBytes !== "number" ||
    !Number.isFinite(chunkSizeBytes) ||
    chunkSizeBytes <= 0
  ) {
    throw new Error(
      "Download chunk size must be a numeric value greater than 0."
    );
  }

  const createdAt = new Date().toISOString();
  const chunkIds = new Set();
  const chunks = [];

  files.forEach((fileRecord) => {
    validateFileSize(fileRecord);

    for (let start = 0; start < fileRecord.size; start += chunkSizeBytes) {
      const end = Math.min(start + chunkSizeBytes - 1, fileRecord.size - 1);
      const chunkId = `${fileRecord.id}:${start}-${end}`;

      if (chunkIds.has(chunkId)) {
        throw new Error(`Duplicate download chunk id: ${chunkId}`);
      }

      chunkIds.add(chunkId);
      chunks.push({
        id: chunkId,
        jobId,
        fileRecordId: fileRecord.id,
        start,
        end,
        size: end - start + 1,
        status: "pending",
        attempts: 0,
        createdAt,
        updatedAt: createdAt,
      });
    }
  });

  return chunks;
}

function waitForTransaction(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      reject(
        transaction.error ||
          new Error("Unable to create download job persistence records.")
      );
    };
    transaction.onabort = () => {
      reject(
        transaction.error ||
          new Error("Download job persistence transaction was aborted.")
      );
    };
  });
}

function waitForRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = () => {
      reject(request.error || new Error("Download persistence request failed."));
    };
  });
}

function waitForDeleteCursor({ store, jobId, useJobIdIndex = true }) {
  return new Promise((resolve, reject) => {
    let deletedCount = 0;
    let request;
    let usingIndexCursor = false;

    if (useJobIdIndex && typeof store.index === "function") {
      const jobIdIndex = store.index("jobId");

      if (jobIdIndex && typeof jobIdIndex.openCursor === "function") {
        request = jobIdIndex.openCursor(jobId);
        usingIndexCursor = true;
      }
    }

    if (!request) {
      request = store.openCursor();
    }

    request.onsuccess = (event) => {
      const cursor = event.target.result;

      if (!cursor) {
        resolve(deletedCount);
        return;
      }

      if (!usingIndexCursor && cursor.value.jobId !== jobId) {
        cursor.continue();
        return;
      }

      deletedCount += 1;
      cursor.delete();
      cursor.continue();
    };
    request.onerror = () => {
      reject(request.error || new Error("Unable to delete download records."));
    };
  });
}

function getAllFromStore(store) {
  if (typeof store.getAll === "function") {
    return waitForRequest(store.getAll());
  }

  return new Promise((resolve, reject) => {
    const records = [];
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;

      if (!cursor) {
        resolve(records);
        return;
      }

      records.push(cursor.value);
      cursor.continue();
    };
    request.onerror = () => {
      reject(request.error || new Error("Unable to read download records."));
    };
  });
}

function getAllByJobId(store, jobId) {
  if (typeof store.index === "function") {
    const jobIdIndex = store.index("jobId");

    if (jobIdIndex && typeof jobIdIndex.getAll === "function") {
      return waitForRequest(jobIdIndex.getAll(jobId));
    }
  }

  return getAllFromStore(store).then((records) =>
    records.filter((record) => record.jobId === jobId)
  );
}

function getAllByFileRecordId(store, fileRecordId) {
  if (typeof store.index === "function") {
    const fileRecordIdIndex = store.index("fileRecordId");

    if (
      fileRecordIdIndex &&
      typeof fileRecordIdIndex.getAll === "function"
    ) {
      return waitForRequest(fileRecordIdIndex.getAll(fileRecordId));
    }
  }

  return getAllFromStore(store).then((records) =>
    records.filter((record) => record.fileRecordId === fileRecordId)
  );
}

function sortJobsByUpdatedAtDescending(jobs) {
  return jobs.sort((a, b) => {
    const aTime = Date.parse(a.updatedAt || "") || 0;
    const bTime = Date.parse(b.updatedAt || "") || 0;

    return bTime - aTime;
  });
}

function assertDatabase(db) {
  if (!db || typeof db.transaction !== "function") {
    throw new Error("A valid IndexedDB database is required.");
  }
}

function emptyDeleteResult() {
  return {
    deletedJob: false,
    deletedFiles: 0,
    deletedChunks: 0,
    deletedEvents: 0,
  };
}

function combineDeleteResults(results) {
  return results.reduce(
    (totals, result) => ({
      jobsDeleted: totals.jobsDeleted + (result.deletedJob ? 1 : 0),
      filesDeleted: totals.filesDeleted + result.deletedFiles,
      chunksDeleted: totals.chunksDeleted + result.deletedChunks,
      eventsDeleted: totals.eventsDeleted + result.deletedEvents,
    }),
    {
      jobsDeleted: 0,
      filesDeleted: 0,
      chunksDeleted: 0,
      eventsDeleted: 0,
    }
  );
}

export function openDownloadPersistence({
  indexedDBImpl = typeof indexedDB !== "undefined" ? indexedDB : undefined,
} = {}) {
  return new Promise((resolve, reject) => {
    if (!indexedDBImpl || typeof indexedDBImpl.open !== "function") {
      reject(new Error("IndexedDB is not available for download persistence."));
      return;
    }

    const request = indexedDBImpl.open(DOWNLOAD_DB_NAME, DOWNLOAD_DB_VERSION);

    request.onupgradeneeded = (event) => {
      ensureSchema(event.target.result, event.target.transaction);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onerror = () => {
      reject(
        request.error ||
          new Error("Unable to open download persistence IndexedDB database.")
      );
    };

    request.onblocked = () => {
      reject(
        new Error(
          "Download persistence database upgrade is blocked by another tab."
        )
      );
    };
  });
}

export async function createDownloadJob({
  db,
  job,
  files,
  chunkSizeBytes = DEFAULT_CHUNK_SIZE_BYTES,
}) {
  if (!db || typeof db.transaction !== "function") {
    throw new Error(
      "A valid IndexedDB database is required to create a download job."
    );
  }

  if (!job || !job.id) {
    throw new Error("Download job must include an id.");
  }

  if (!Array.isArray(files)) {
    throw new Error("Download job files must be an array.");
  }

  validateUniqueFileIds(files);

  const chunks = buildChunkRecords({
    jobId: job.id,
    files,
    chunkSizeBytes,
  });

  const transaction = db.transaction(
    [DOWNLOAD_STORES.jobs, DOWNLOAD_STORES.files, DOWNLOAD_STORES.chunks],
    "readwrite"
  );

  const jobStore = transaction.objectStore(DOWNLOAD_STORES.jobs);
  const fileStore = transaction.objectStore(DOWNLOAD_STORES.files);
  const chunkStore = transaction.objectStore(DOWNLOAD_STORES.chunks);
  const transactionComplete = waitForTransaction(transaction);

  jobStore.add(job);
  files.forEach((fileRecord) => {
    fileStore.add(fileRecord);
  });
  chunks.forEach((chunkRecord) => {
    chunkStore.add(chunkRecord);
  });

  await transactionComplete;

  return {
    jobId: job.id,
    fileCount: files.length,
    chunkCount: chunks.length,
  };
}

export async function getDownloadJob({ db, jobId }) {
  assertDatabase(db);

  const transaction = db.transaction(
    [DOWNLOAD_STORES.jobs, DOWNLOAD_STORES.files, DOWNLOAD_STORES.chunks],
    "readonly"
  );
  const jobStore = transaction.objectStore(DOWNLOAD_STORES.jobs);
  const fileStore = transaction.objectStore(DOWNLOAD_STORES.files);
  const chunkStore = transaction.objectStore(DOWNLOAD_STORES.chunks);

  const [job, files, chunks] = await Promise.all([
    waitForRequest(jobStore.get(jobId)),
    getAllByJobId(fileStore, jobId),
    getAllByJobId(chunkStore, jobId),
  ]);

  if (!job) {
    return null;
  }

  return {
    job,
    files,
    chunks,
  };
}

export async function listIncompleteDownloadJobs({ db }) {
  assertDatabase(db);

  const transaction = db.transaction([DOWNLOAD_STORES.jobs], "readonly");
  const jobs = await getAllFromStore(
    transaction.objectStore(DOWNLOAD_STORES.jobs)
  );

  return sortJobsByUpdatedAtDescending(
    jobs.filter((job) => !TERMINAL_JOB_STATUSES.has(job.status))
  );
}

export async function normalizeIncompleteDownloadJobs({ db }) {
  assertDatabase(db);

  const readTransaction = db.transaction(
    [DOWNLOAD_STORES.jobs, DOWNLOAD_STORES.files, DOWNLOAD_STORES.chunks],
    "readonly"
  );
  const readJobStore = readTransaction.objectStore(DOWNLOAD_STORES.jobs);
  const readFileStore = readTransaction.objectStore(DOWNLOAD_STORES.files);
  const readChunkStore = readTransaction.objectStore(DOWNLOAD_STORES.chunks);
  const [jobs, files, chunks] = await Promise.all([
    getAllFromStore(readJobStore),
    getAllFromStore(readFileStore),
    getAllFromStore(readChunkStore),
  ]);
  const updatedAt = new Date().toISOString();
  const jobsToUpdate = jobs
    .filter((job) => job.status === "running" || job.status === "queued")
    .map((job) => ({
      ...job,
      status: "paused",
      updatedAt,
    }));
  const filesToUpdate = files
    .filter(
      (fileRecord) =>
        fileRecord.status === "running" ||
        fileRecord.status === "creating-file"
    )
    .map((fileRecord) => ({
      ...fileRecord,
      status: "paused",
      updatedAt,
    }));
  const chunksToUpdate = chunks
    .filter((chunkRecord) => chunkRecord.status === "running")
    .map((chunkRecord) => ({
      ...chunkRecord,
      status: "pending",
      updatedAt,
    }));

  if (
    jobsToUpdate.length > 0 ||
    filesToUpdate.length > 0 ||
    chunksToUpdate.length > 0
  ) {
    const writeTransaction = db.transaction(
      [DOWNLOAD_STORES.jobs, DOWNLOAD_STORES.files, DOWNLOAD_STORES.chunks],
      "readwrite"
    );
    const writeJobStore = writeTransaction.objectStore(DOWNLOAD_STORES.jobs);
    const writeFileStore = writeTransaction.objectStore(DOWNLOAD_STORES.files);
    const writeChunkStore = writeTransaction.objectStore(DOWNLOAD_STORES.chunks);
    const transactionComplete = waitForTransaction(writeTransaction);

    jobsToUpdate.forEach((job) => {
      writeJobStore.put(job);
    });
    filesToUpdate.forEach((fileRecord) => {
      writeFileStore.put(fileRecord);
    });
    chunksToUpdate.forEach((chunkRecord) => {
      writeChunkStore.put(chunkRecord);
    });

    await transactionComplete;
  }

  return {
    jobsUpdated: jobsToUpdate.length,
    filesUpdated: filesToUpdate.length,
    chunksUpdated: chunksToUpdate.length,
  };
}

export async function recalculateDownloadJobProgress({ db, jobId }) {
  assertDatabase(db);

  const readTransaction = db.transaction(
    [DOWNLOAD_STORES.jobs, DOWNLOAD_STORES.files],
    "readonly"
  );
  const readJobStore = readTransaction.objectStore(DOWNLOAD_STORES.jobs);
  const readFileStore = readTransaction.objectStore(DOWNLOAD_STORES.files);
  const [job, files] = await Promise.all([
    waitForRequest(readJobStore.get(jobId)),
    getAllByJobId(readFileStore, jobId),
  ]);

  if (!job) {
    return null;
  }

  const updatedJob = {
    ...job,
    completedBytes: files.reduce(
      (total, fileRecord) => total + (fileRecord.completedBytes || 0),
      0
    ),
    completedFileCount: files.filter(
      (fileRecord) => fileRecord.status === "complete"
    ).length,
    updatedAt: new Date().toISOString(),
  };

  const writeTransaction = db.transaction([DOWNLOAD_STORES.jobs], "readwrite");
  const writeJobStore = writeTransaction.objectStore(DOWNLOAD_STORES.jobs);
  const transactionComplete = waitForTransaction(writeTransaction);

  writeJobStore.put(updatedJob);
  await transactionComplete;

  return updatedJob;
}

export async function updateDownloadFile({ db, file }) {
  assertDatabase(db);

  if (!file || !file.id) {
    throw new Error("Download file record must include an id.");
  }

  const transaction = db.transaction([DOWNLOAD_STORES.files], "readwrite");
  const fileStore = transaction.objectStore(DOWNLOAD_STORES.files);
  const transactionComplete = waitForTransaction(transaction);

  fileStore.put(file);
  await transactionComplete;

  return file;
}

export async function updateDownloadChunksForFile({
  db,
  fileRecordId,
  updates,
}) {
  assertDatabase(db);

  if (!fileRecordId) {
    throw new Error("A download file record id is required.");
  }

  const readTransaction = db.transaction([DOWNLOAD_STORES.chunks], "readonly");
  const chunks = await getAllByFileRecordId(
    readTransaction.objectStore(DOWNLOAD_STORES.chunks),
    fileRecordId
  );
  const updatedChunks = chunks.map((chunkRecord) => ({
    ...chunkRecord,
    ...(updates || {}),
  }));

  if (updatedChunks.length > 0) {
    const writeTransaction = db.transaction(
      [DOWNLOAD_STORES.chunks],
      "readwrite"
    );
    const chunkStore = writeTransaction.objectStore(DOWNLOAD_STORES.chunks);
    const transactionComplete = waitForTransaction(writeTransaction);

    updatedChunks.forEach((chunkRecord) => {
      chunkStore.put(chunkRecord);
    });

    await transactionComplete;
  }

  return updatedChunks.length;
}

export async function updateDownloadChunk({ db, chunk }) {
  assertDatabase(db);

  if (!chunk || !chunk.id) {
    throw new Error("Download chunk record must include an id.");
  }

  const transaction = db.transaction([DOWNLOAD_STORES.chunks], "readwrite");
  const chunkStore = transaction.objectStore(DOWNLOAD_STORES.chunks);
  const transactionComplete = waitForTransaction(transaction);

  chunkStore.put(chunk);
  await transactionComplete;

  return chunk;
}

export async function updateDownloadJob({ db, job }) {
  assertDatabase(db);

  if (!job || !job.id) {
    throw new Error("Download job record must include an id.");
  }

  const transaction = db.transaction([DOWNLOAD_STORES.jobs], "readwrite");
  const jobStore = transaction.objectStore(DOWNLOAD_STORES.jobs);
  const transactionComplete = waitForTransaction(transaction);

  jobStore.put(job);
  await transactionComplete;

  return job;
}

export async function cancelDownloadJob({ db, jobId }) {
  assertDatabase(db);

  if (!jobId) {
    throw new Error("A download job id is required.");
  }

  const readTransaction = db.transaction([DOWNLOAD_STORES.jobs], "readonly");
  const jobStore = readTransaction.objectStore(DOWNLOAD_STORES.jobs);
  const job = await waitForRequest(jobStore.get(jobId));

  if (!job) {
    return null;
  }

  if (TERMINAL_JOB_STATUSES.has(job.status)) {
    return job;
  }

  const cancelledAt = new Date().toISOString();
  const cancelledJob = {
    ...job,
    status: "cancelled",
    cancelledAt,
    updatedAt: cancelledAt,
  };
  const writeTransaction = db.transaction([DOWNLOAD_STORES.jobs], "readwrite");
  const writeJobStore = writeTransaction.objectStore(DOWNLOAD_STORES.jobs);
  const transactionComplete = waitForTransaction(writeTransaction);

  writeJobStore.put(cancelledJob);
  await transactionComplete;

  return cancelledJob;
}

export async function deleteDownloadJob({ db, jobId }) {
  assertDatabase(db);

  const transaction = db.transaction(
    [
      DOWNLOAD_STORES.jobs,
      DOWNLOAD_STORES.files,
      DOWNLOAD_STORES.chunks,
      DOWNLOAD_STORES.events,
    ],
    "readwrite"
  );
  const jobStore = transaction.objectStore(DOWNLOAD_STORES.jobs);
  const fileStore = transaction.objectStore(DOWNLOAD_STORES.files);
  const chunkStore = transaction.objectStore(DOWNLOAD_STORES.chunks);
  const eventStore = transaction.objectStore(DOWNLOAD_STORES.events);
  const transactionComplete = waitForTransaction(transaction);
  const deleteResult = new Promise((resolve, reject) => {
    const request = jobStore.get(jobId);

    request.onsuccess = (event) => {
      if (!event.target.result) {
        resolve(emptyDeleteResult());
        return;
      }

      jobStore.delete(jobId);

      Promise.all([
        waitForDeleteCursor({ store: fileStore, jobId }),
        waitForDeleteCursor({ store: chunkStore, jobId }),
        waitForDeleteCursor({ store: eventStore, jobId }),
      ]).then(
        ([deletedFiles, deletedChunks, deletedEvents]) => {
          resolve({
            deletedJob: true,
            deletedFiles,
            deletedChunks,
            deletedEvents,
          });
        },
        reject
      );
    };

    request.onerror = () => {
      reject(request.error || new Error("Unable to read download job."));
    };
  });
  const result = await deleteResult;

  await transactionComplete;

  return result;
}

export async function deleteCompletedDownloadJobs({ db }) {
  assertDatabase(db);

  const transaction = db.transaction([DOWNLOAD_STORES.jobs], "readonly");
  const jobs = await getAllFromStore(
    transaction.objectStore(DOWNLOAD_STORES.jobs)
  );
  const results = [];

  for (const job of jobs.filter((record) => record.status === "complete")) {
    results.push(await deleteDownloadJob({ db, jobId: job.id }));
  }

  return combineDeleteResults(results);
}

export async function deleteCancelledDownloadJobs({ db }) {
  assertDatabase(db);

  const transaction = db.transaction([DOWNLOAD_STORES.jobs], "readonly");
  const jobs = await getAllFromStore(
    transaction.objectStore(DOWNLOAD_STORES.jobs)
  );
  const results = [];

  for (const job of jobs.filter((record) => record.status === "cancelled")) {
    results.push(await deleteDownloadJob({ db, jobId: job.id }));
  }

  return combineDeleteResults(results);
}

export async function getDownloadPersistenceStatistics({ db }) {
  assertDatabase(db);

  const transaction = db.transaction(
    [
      DOWNLOAD_STORES.jobs,
      DOWNLOAD_STORES.files,
      DOWNLOAD_STORES.chunks,
      DOWNLOAD_STORES.events,
    ],
    "readonly"
  );
  const [jobs, files, chunks, events] = await Promise.all([
    getAllFromStore(transaction.objectStore(DOWNLOAD_STORES.jobs)),
    getAllFromStore(transaction.objectStore(DOWNLOAD_STORES.files)),
    getAllFromStore(transaction.objectStore(DOWNLOAD_STORES.chunks)),
    getAllFromStore(transaction.objectStore(DOWNLOAD_STORES.events)),
  ]);

  return {
    jobs: jobs.length,
    files: files.length,
    chunks: chunks.length,
    events: events.length,
    activeJobs: jobs.filter((job) => !TERMINAL_JOB_STATUSES.has(job.status))
      .length,
    completedJobs: jobs.filter((job) => job.status === "complete").length,
    cancelledJobs: jobs.filter((job) => job.status === "cancelled").length,
  };
}
